import { isProd } from "src/utilities/isProd";
import { getUserContext } from "./getUserContext";
import { onOpen } from "./onOpen";
import { onSecretExpiry } from "./onSecretExpiry";
import getQueryParamValue from "src/utilities/getQueryParamValue";
import { onAgentMessageEnd } from "./onAgentMessageEnd";
import getTranslations from "src/i18n";

export const getSierraConfig = async () => {
  const userContext = await getUserContext();
  const customSierraVar = getCustomSierraVariables();
  const translations = await getTranslations(userContext.language);

  const sierraConfig = {
    display: "custom",
    customContainer: document.getElementById("sierra-chat-container"),
    customGreeting: translations["cchp_ai_chat_greeting_message"].replace(
      "%{firstname}",
      `${userContext.firstName}`
    ),
    persistence: "tab",
    disableAutoFocus: true,
    canEndConversation: true,
    chatInterfaceStrings: {
      name: translations["cchp_ai_chat_header"],
      printTranscriptMenuLabel:
        translations["cchp_ai_chat_print_transcript_button"],
      disclosure: translations["cchp_ai_chat_disclosure"],
      endConversationMenuLabel: translations["cchp_ai_chat_end_chat_button"],
      errorMessage: translations["cchp_ai_chat_error_message"],
      conversationEndedMessage: translations["cchp_ai_chat_end_message"],
      agentTransferWaitingMessage:
        translations["cchp_ai_chat_agent_transfer_waiting_message"],
      agentTransferQueueNextMessage:
        translations["cchp_ai_chat_agent_transfer_queue_next_message"],
      agentTransferQueueSizeMessage:
        translations["cchp_ai_chat_agent_transfer_queue_size_message"],
      agentJoinedMessage:
        translations["cchp_ai_chat_agent_transfer_join_message"],
      agentLeftMessage: translations["cchp_ai_chat_agent_left_message"],
      newChatButtonLabel: translations["cchp_ai_chat_start_new_chat"],
      inputPlaceholder: translations["cchp_ai_chat_message_placeholder"],
    },
    canPrintTranscript: true,
    canStartNewChat: true,
    unstyled: true,
    variables: {
      shop: userContext?.shop,
      country: userContext?.country,
      language: userContext?.language,
      storeId: userContext?.store,
      environment: isProd() ? "production" : "staging",
      isAuthenticated: userContext?.isAuthenticated,
      customerId: userContext?.customerId,
      ...(userContext?.isAuthenticated
        ? { firstName: userContext?.firstName, lastName: userContext.lastName }
        : {}),
      ...customSierraVar,
    },
    secrets: {
      accessToken: userContext?.accessToken,
    },
    onSecretExpiry: (secretName) => {
      onSecretExpiry(secretName);
    },
    onClose: () => {
      window?.asos?.sierra?.onClose();
    },
    onOpen: (opts) => {
      onOpen(opts, userContext);
    },
    onAgentMessageEnd: () => {
      onAgentMessageEnd();
    },
  };

  return sierraConfig;
};

/**
 * Get the sierra-var query parameter to extract multiple key:value pairs
 * Keys must be valid JSON property names (starting with letter or underscore, followed by letters, numbers, or underscores)
 * Values must contain only alphanumeric characters, underscores (_), and hyphens (-)
 * @returns {Object} Parsed key-value pairs
 */
const getCustomSierraVariables = () => {
  const sierraRawVar = getQueryParamValue("sierra-var"); // example: ?sierra-var=key1:value1,key2:value2,key3:value3
  const customSierraVar = {};
  if (!sierraRawVar) {
    return customSierraVar;
  }

  const pairs = sierraRawVar.split(",");
  const validKeyRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/; // Valid JSON property name (no $ sign)
  const validValueRegex = /^[a-zA-Z0-9_-]+$/; // Alphanumeric with _ and -

  pairs.forEach((pair) => {
    const trimmedPair = pair.trim();
    if (trimmedPair.includes(":")) {
      const colonIndex = trimmedPair.indexOf(":");
      const key = trimmedPair.substring(0, colonIndex).trim();
      const value = trimmedPair.substring(colonIndex + 1).trim();

      // Validate key is a valid JSON property name and value contains only allowed characters
      if (key && validKeyRegex.test(key) && validValueRegex.test(value)) {
        customSierraVar[key] = value;
      }
    }
  });

  return customSierraVar;
};
